import { useMemo } from "react";
import type { AgentResponse } from "@/api";
import {
  addAgentTeamMember,
  addAgentUserMember,
  getAgentMembersOptions,
  getAgentMembersQueryKey,
  removeAgentTeamMember,
  removeAgentUserMember,
  updateAgentTeamMember,
  updateAgentUserMember,
} from "@/api";
import { type MembershipApi, MembershipPanel } from "@/components/build/MembershipPanel";
import { useCurrentUser } from "@/features/account/useCurrentUser";

/** Agents' Access tab: the membership model, via the shared MembershipPanel. */
export function AgentAccessTab({ agent }: Readonly<{ agent: AgentResponse }>) {
  const me = useCurrentUser();

  const api = useMemo<MembershipApi>(
    () => ({
      membersOptions: () => getAgentMembersOptions({ path: { agent_uuid: agent.uuid } }),
      membersQueryKey: () => getAgentMembersQueryKey({ path: { agent_uuid: agent.uuid } }),
      addUser: (id, role) =>
        addAgentUserMember({ path: { agent_uuid: agent.uuid }, body: { userUuid: id, role }, throwOnError: true }),
      updateUserRole: (id, role) =>
        updateAgentUserMember({ path: { agent_uuid: agent.uuid, user_uuid: id }, body: { role }, throwOnError: true }),
      removeUser: (id) =>
        removeAgentUserMember({ path: { agent_uuid: agent.uuid, user_uuid: id }, throwOnError: true }),
      updateTeamRole: (id, role) =>
        updateAgentTeamMember({ path: { agent_uuid: agent.uuid, team_uuid: id }, body: { role }, throwOnError: true }),
      removeTeam: (id) =>
        removeAgentTeamMember({ path: { agent_uuid: agent.uuid, team_uuid: id }, throwOnError: true }),
      addTeam: (id, role) =>
        addAgentTeamMember({ path: { agent_uuid: agent.uuid }, body: { teamUuid: id, role }, throwOnError: true }),
    }),
    [agent.uuid],
  );

  return <MembershipPanel api={api} currentUserUuid={me.data?.user.uuid} />;
}
